import { useEffect, useMemo, useState } from 'react';
import { Save, Package } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { Textarea } from '@/components/form/Textarea';
import { SmartSelect } from '@/components/form/SmartSelect';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import { useAuth } from '@/auth/useAuth';
import {
  createMrn,
  updateMrn,
  type Mrn,
  type MrnItem,
  type Priority,
  type Authority,
} from '@/api/mrn';
import { listItems } from '@/api/items';
import { todayISO } from '@/lib/format';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  editing: Mrn | null;
  onClose: () => void;
  /** Pre-fill the line items when creating a new MRN (e.g. raised from the cart). */
  seedItems?: MrnItem[];
}

type FormState = {
  mrnDate: string;
  subject: string;
  purpose: string;
  refDoc: string;
  department: string;
  raisedBy: string;
  priority: Priority;
  requiredBy: string;
  approvalAuthority: Authority;
  remarks: string;
  items: MrnItem[];
};

function emptyForm(): FormState {
  return {
    mrnDate: todayISO(),
    subject: '',
    purpose: 'Stock Replenishment',
    refDoc: '',
    department: '',
    raisedBy: '',
    priority: 'High',
    requiredBy: '',
    approvalAuthority: 'HOD',
    remarks: '',
    items: [{ itemName: '', qty: '', unit: '', remarks: '' }],
  };
}

function fromMrn(m: Mrn): FormState {
  return {
    mrnDate: m.mrnDate.slice(0, 10),
    subject: m.subject ?? '',
    purpose: m.purpose,
    refDoc: m.refDoc ?? '',
    department: m.department,
    raisedBy: m.raisedBy,
    priority: m.priority,
    requiredBy: m.requiredBy ? m.requiredBy.slice(0, 10) : '',
    approvalAuthority: m.approvalAuthority,
    remarks: m.remarks ?? '',
    items: m.items.length
      ? m.items.map((it) => ({
          itemId: it.itemId,
          itemName: it.itemName,
          qty: it.qty,
          unit: it.unit,
          remarks: it.remarks ?? '',
        }))
      : [{ itemName: '', qty: '', unit: '', remarks: '' }],
  };
}

export function MrnFormModal({ open, editing, onClose, seedItems }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(fromMrn(editing));
    } else {
      const base = emptyForm();
      base.raisedBy = user?.fullName ?? '';
      if (seedItems && seedItems.length) base.items = seedItems.map((s) => ({ ...s }));
      setForm(base);
    }
  }, [open, editing, user?.fullName, seedItems]);

  // Pull a small item-master sample to power the per-row item dropdown.
  const { data: itemsResp } = useQuery({
    queryKey: ['items', { pageSize: 10000, status: 'Active' }],
    queryFn: () => listItems({ pageSize: 10000, status: 'Active' }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const itemMaster = useMemo(() => itemsResp?.data ?? [], [itemsResp]);

  const editingId = editing?.id ?? null;
  const isReadOnly = editing
    ? editing.status !== 'Draft' && editing.status !== 'Pending' && editing.status !== 'Pending HOD'
    : false;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      return editingId ? updateMrn(editingId, payload) : createMrn(payload);
    },
    onSuccess: (mrn) => {
      qc.invalidateQueries({ queryKey: ['mrn'] });
      qc.invalidateQueries({ queryKey: ['mrn-pending'] });
      if (editingId) {
        toast.push(`MRN ${mrn.mrnNo} updated`, 'success');
      } else {
        toast.pushStep(`MRN ${mrn.mrnNo} created`, {
          body: 'Next step — submit for HOD approval.',
          href: '/purchase/mrn',
          nextLabel: 'Open MRN list',
          tag: `mrn-created-${mrn.id}`,
        });
      }
      onClose();
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  function onSubmit() {
    const validationErr = validate(form);
    if (validationErr) {
      toast.push(validationErr, 'error');
      return;
    }
    saveMutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingId ? `Edit MRN — ${editing?.mrnNo}` : 'New Material Requisition'}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending || isReadOnly}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save MRN</span>)}
          </Button>
        </>
      }
    >
      {editing?.source === 'Sales-Short' && (
        <div className="mb-3 rounded-md border border-purple-300/30 bg-purple-400/[0.08] px-3.5 py-2.5 text-[13px] font-semibold text-[#a78bfa]">
          <Package size={13} className="mr-1 inline-block align-[-2px]" /> Auto-raised from {editing.enqRef ?? 'Sales'} — sales shortfall MRN
        </div>
      )}
      {editing?.status === 'Rejected' && editing.rejectionReason && (
        <div className="mb-3 rounded-md border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-[13px] text-danger">
          <strong>Rejection reason:</strong> {editing.rejectionReason}
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5 sm:gap-3">
        <FormGroup label="MRN Number">
          <Input value={editing?.mrnNo ?? 'Auto'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input
            type="date"
            value={form.mrnDate}
            onChange={(e) => setForm({ ...form, mrnDate: e.target.value })}
            disabled={isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Subject" className="col-span-2">
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Short summary, e.g. Diwali bedsheet restock — carried to PR, PO and GRN"
            maxLength={200}
            disabled={isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Purpose / Type" required>
          <Select
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            disabled={isReadOnly}
          >
            <option>Stock Replenishment</option>
            <option>Sales Shortfall</option>
            <option>New Product Launch</option>
            <option>Showroom Display</option>
            <option>Hotel/Institutional</option>
            <option>Export Order</option>
            <option>Office Use</option>
            <option>Other</option>
          </Select>
        </FormGroup>
        <FormGroup label="Reference (ENQ / Order No.)">
          <Input
            value={form.refDoc}
            onChange={(e) => setForm({ ...form, refDoc: e.target.value })}
            placeholder="Optional"
            disabled={isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Department" required>
          <SmartSelect
            listKey="dept"
            value={form.department}
            onChange={(v) => setForm({ ...form, department: v })}
            disabled={isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Raised By" required>
          <Input
            value={form.raisedBy}
            readOnly
            className="opacity-80 cursor-default"
          />
        </FormGroup>
        <FormGroup label="Priority">
          <Select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
            disabled={isReadOnly}
          >
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Urgent</option>
          </Select>
        </FormGroup>
        <FormGroup label="Required By Date">
          <Input
            type="date"
            value={form.requiredBy}
            onChange={(e) => setForm({ ...form, requiredBy: e.target.value })}
            disabled={isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Approval Authority">
          <Select
            value={form.approvalAuthority}
            onChange={(e) =>
              setForm({ ...form, approvalAuthority: e.target.value as Authority })
            }
            disabled={isReadOnly}
          >
            <option>HOD</option>
            <option>Manager</option>
            <option>Director</option>
            <option>Chairman</option>
          </Select>
        </FormGroup>
        <FormGroup label="Remarks / Justification" className="col-span-2">
          <Textarea
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            placeholder="Why is this material required?"
            disabled={isReadOnly}
          />
        </FormGroup>
      </div>

      <ItemsTable<MrnItem>
        title="Items Required"
        rows={form.items}
        onChange={(items) => setForm({ ...form, items })}
        newRow={() => ({ itemName: '', qty: '', unit: '', remarks: '' })}
        columns={[
          {
            key: 'item',
            header: 'Item',
            cell: (row, _i, update) => (
              <CategoryItemPicker
                showImage
                items={itemMaster.map((it) => ({
                  id: it.id,
                  code: it.code,
                  name: it.name,
                  category: it.category,
                  currentStock: 0,
                  imageUrl: it.imageUrl,
                  barcode: it.barcode,
                }))}
                value={row.itemId ?? '__custom__'}
                disabled={isReadOnly}
                onCustom={() => update({ itemId: null })}
                onPick={(id) => {
                  const it = itemMaster.find((i) => i.id === id);
                  update({
                    itemId: id,
                    itemName: it?.name ?? row.itemName,
                    unit: it?.unit ?? row.unit,
                  });
                }}
              />
            ),
          },
          {
            key: 'itemName',
            header: 'Description',
            cell: (row, _i, update) => (
              <Input
                value={row.itemName}
                onChange={(e) => update({ itemName: e.target.value })}
                disabled={isReadOnly}
                placeholder="Item name"
              />
            ),
          },
          {
            key: 'qty',
            header: 'Qty',
            width: '90px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={row.qty as string}
                onChange={(e) => update({ qty: e.target.value })}
                disabled={isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'unit',
            header: 'Unit',
            width: '110px',
            cell: (row, _i, update) => (
              <Input
                value={row.unit}
                onChange={(e) => update({ unit: e.target.value })}
                disabled={isReadOnly}
                placeholder="Pcs / Mtrs"
              />
            ),
          },
          {
            key: 'remarks',
            header: 'Remarks',
            cell: (row, _i, update) => (
              <Input
                value={row.remarks ?? ''}
                onChange={(e) => update({ remarks: e.target.value })}
                disabled={isReadOnly}
                placeholder="Optional"
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}

function toPayload(f: FormState) {
  return {
    mrnDate: f.mrnDate,
    subject: f.subject.trim(),
    purpose: f.purpose,
    refDoc: f.refDoc.trim() || undefined,
    department: f.department,
    raisedBy: f.raisedBy.trim(),
    priority: f.priority,
    requiredBy: f.requiredBy || undefined,
    approvalAuthority: f.approvalAuthority,
    remarks: f.remarks.trim() || undefined,
    items: f.items
      .filter((it) => it.itemName.trim() && Number(it.qty) > 0)
      .map((it) => ({
        itemId: it.itemId ?? undefined,
        itemName: it.itemName.trim(),
        qty: Number(it.qty),
        unit: it.unit.trim(),
        remarks: (it.remarks ?? '').trim() || undefined,
      })),
  };
}

function validate(f: FormState): string | null {
  if (!f.department) return 'Department is required';
  if (!f.raisedBy.trim()) return '"Raised By" is required';
  const validItems = f.items.filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim());
  if (validItems.length === 0) return 'Add at least one valid item (name + qty + unit)';
  return null;
}
